import { supabase } from "@/integrations/supabase/client";

type LabRow = {
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
};

type DocumentRow = {
  file_name: string;
  ai_summary: string | null;
  content_kind: string | null;
  created_at: string;
};

export type DeterministicAnswer =
  | { handled: false }
  | { handled: true; answer: string };

const LAB_KEYWORDS = [
  "ferritina",
  "glicose",
  "glicemia",
  "hemoglobina glicada",
  "hba1c",
  "colesterol",
  "hdl",
  "ldl",
  "vldl",
  "triglicerideos",
  "triglicerides",
  "creatinina",
  "ureia",
  "tsh",
  "t3",
  "t4",
  "vitamina d",
  "vitamina b12",
  "ferro",
  "hemoglobina",
  "hematocrito",
  "leucocitos",
  "plaquetas",
  "testosterona",
  "cortisol",
  "psa",
  "acido urico",
];

type ImagingMatcher = {
  label: string;
  patterns: string[];
};

const IMAGING_MATCHERS: ImagingMatcher[] = [
  { label: "tomografia", patterns: ["tomografia", " tc ", "tc de ", "tomograf"] },
  {
    label: "ressonância magnética",
    patterns: ["ressonancia", "ressonância", " rm ", "rm de ", "rnm"],
  },
  {
    label: "ultrassonografia",
    patterns: ["ultrassom", "ultrassonografia", "usg", "ecografia", "eco abdom"],
  },
  { label: "raio-x", patterns: ["raio x", "raio-x", "rx ", "radiografia"] },
  { label: "endoscopia", patterns: ["endoscopia"] },
  { label: "colonoscopia", patterns: ["colonoscopia"] },
  { label: "biópsia", patterns: ["biopsia", "biópsia"] },
  { label: "mamografia", patterns: ["mamografia"] },
  { label: "densitometria", patterns: ["densitometria"] },
  { label: "ecocardiograma", patterns: ["ecocardiograma", "ecocardio"] },
  {
    label: "eletrocardiograma",
    patterns: ["eletrocardiograma", "eletrocardio", " ecg ", "ecg"],
  },
  { label: "laudo médico", patterns: ["laudo"] },
  { label: "prescrição", patterns: ["prescricao", "prescrição", "receita"] },
];

const GENERIC_LAB_PATTERNS = [
  "exame de sangue",
  "exames de sangue",
  "exame laboratorial",
  "exames laboratoriais",
  "exames de laboratorio",
  "exame de laboratorio",
  "ultimo exame",
  "ultimos exames",
  "ultimos resultados",
  "ultimo resultado",
  "resultados laboratoriais",
  "hemograma",
];

function normalizeText(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function detectLabKeyword(query: string): string | null {
  const normalized = normalizeText(query);
  for (const keyword of LAB_KEYWORDS) {
    if (normalized.includes(keyword)) return keyword;
  }
  return null;
}

function detectImagingMatcher(query: string): ImagingMatcher | null {
  const normalized = ` ${normalizeText(query)} `;
  for (const matcher of IMAGING_MATCHERS) {
    for (const pattern of matcher.patterns) {
      if (normalized.includes(normalizeText(pattern))) {
        return matcher;
      }
    }
  }
  return null;
}

function matchesGenericLabQuery(query: string): boolean {
  const normalized = normalizeText(query);
  return GENERIC_LAB_PATTERNS.some((p) => normalized.includes(p));
}

function parseDateSafe(value: string | null | undefined): number {
  if (!value) return 0;
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? 0 : ts;
}

function formatDatePtBr(value: string | null | undefined): string {
  if (!value) return "data não informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "data não informada";
  return date.toLocaleDateString("pt-BR");
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
}

function classifyValue(
  value: number,
  refLow: number | null,
  refHigh: number | null,
): string | null {
  if (refLow != null && value < refLow) return "abaixo do valor de referência";
  if (refHigh != null && value > refHigh) return "acima do valor de referência";
  if (refLow != null && refHigh != null) return "dentro do valor de referência";
  return null;
}

async function resolvePatientIds(userId: string): Promise<string[]> {
  const { data } = await supabase
    .from("patients_onboarding")
    .select("id")
    .eq("owner_id", userId)
    .order("created_at", { ascending: true });

  return (data ?? [])
    .map((p) => p.id)
    .filter((id): id is string => !!id);
}

async function fetchAllLabs(patientIds: string[]): Promise<LabRow[] | null> {
  const { data, error } = await supabase
    .from("lab_results_onboarding")
    .select("test_name, value, unit, ref_low, ref_high, collected_at")
    .in("patient_id", patientIds)
    .not("value", "is", null)
    .order("collected_at", { ascending: true })
    .limit(1000);

  if (error) return null;
  return (data ?? []) as LabRow[];
}

async function fetchDocuments(userId: string): Promise<DocumentRow[]> {
  const { data } = await supabase
    .from("documents_onboarding")
    .select("file_name, ai_summary, content_kind, created_at")
    .eq("owner_id", userId)
    .order("created_at", { ascending: false })
    .limit(300);
  return (data ?? []) as DocumentRow[];
}

function buildLabKeywordAnswer(keyword: string, labs: LabRow[]): string {
  const matches = labs.filter((row) => {
    if (!row.test_name) return false;
    return normalizeText(row.test_name).includes(keyword);
  });

  if (matches.length === 0) {
    const distinctExams = Array.from(
      new Set(
        labs.map((r) => r.test_name).filter((n): n is string => !!n),
      ),
    ).slice(0, 12);
    const examLines = distinctExams.length
      ? `\n\nExames encontrados no seu histórico:\n- ${distinctExams.join("\n- ")}`
      : "";
    return (
      `Não encontrei registros de ${keyword} no seu histórico laboratorial.` +
      examLines
    );
  }

  const orderedAsc = [...matches].sort(
    (a, b) => parseDateSafe(a.collected_at) - parseDateSafe(b.collected_at),
  );
  const orderedDesc = [...matches].sort(
    (a, b) => parseDateSafe(b.collected_at) - parseDateSafe(a.collected_at),
  );

  const first = orderedAsc[0];
  const latest = orderedDesc[0];
  const unit = latest?.unit || first?.unit || "";

  const firstValue = first?.value ?? 0;
  const latestValue = latest?.value ?? 0;
  const variation = latestValue - firstValue;

  let trendText: string;
  if (matches.length === 1) {
    trendText = "Apenas um registro disponível, sem variação para comparar.";
  } else if (variation > 0) {
    trendText = `Houve aumento de ${formatNumber(variation)} ${unit} entre o primeiro e o último registro.`;
  } else if (variation < 0) {
    trendText = `Houve redução de ${formatNumber(Math.abs(variation))} ${unit} entre o primeiro e o último registro.`;
  } else {
    trendText = "Os valores permaneceram estáveis no período disponível.";
  }

  const latestClassification = classifyValue(
    latestValue,
    latest?.ref_low ?? null,
    latest?.ref_high ?? null,
  );

  const refText =
    latest?.ref_low != null && latest?.ref_high != null
      ? ` (referência: ${formatNumber(latest.ref_low)}${unit ? ` ${unit}` : ""} - ${formatNumber(latest.ref_high)}${unit ? ` ${unit}` : ""})`
      : "";

  const recentLines = orderedDesc
    .slice(0, 6)
    .map(
      (r) =>
        `- ${formatDatePtBr(r.collected_at)}: ${formatNumber(r.value ?? 0)}${r.unit ? ` ${r.unit}` : ""}`,
    )
    .join("\n");

  return [
    `Aqui está a evolução de ${keyword} no seu histórico:`,
    "",
    `- Primeiro registro: ${formatNumber(firstValue)}${unit ? ` ${unit}` : ""} em ${formatDatePtBr(first?.collected_at)}`,
    `- Último registro: ${formatNumber(latestValue)}${unit ? ` ${unit}` : ""} em ${formatDatePtBr(latest?.collected_at)}${latestClassification ? ` — ${latestClassification}${refText}` : ""}`,
    `- ${trendText}`,
    "",
    `Últimos resultados encontrados:`,
    recentLines,
  ].join("\n");
}

function buildGenericLabAnswer(labs: LabRow[]): string {
  if (labs.length === 0) {
    return (
      "Não encontrei exames laboratoriais no seu histórico ainda. " +
      "Assim que você subir um documento de exame, eles aparecerão por aqui."
    );
  }

  const orderedDesc = [...labs].sort(
    (a, b) => parseDateSafe(b.collected_at) - parseDateSafe(a.collected_at),
  );

  const latestDate = orderedDesc[0]?.collected_at ?? null;
  const latestKey = latestDate ? formatDatePtBr(latestDate) : "data mais recente";

  const latestBatch = orderedDesc.filter(
    (r) => formatDatePtBr(r.collected_at) === latestKey,
  );

  const distinctExams = Array.from(
    new Set(
      labs.map((r) => r.test_name).filter((n): n is string => !!n),
    ),
  );

  const latestLines = (latestBatch.length > 0 ? latestBatch : orderedDesc.slice(0, 10))
    .slice(0, 15)
    .map(
      (r) =>
        `- ${r.test_name}: ${formatNumber(r.value ?? 0)}${r.unit ? ` ${r.unit}` : ""}${r.collected_at ? ` (${formatDatePtBr(r.collected_at)})` : ""}`,
    )
    .join("\n");

  return [
    `Seu último exame laboratorial foi em ${latestKey}. Aqui estão os resultados desse dia:`,
    "",
    latestLines,
    "",
    `Ao todo, localizei ${labs.length} medições em ${distinctExams.length} exames diferentes no seu histórico.`,
    `Posso detalhar a evolução de algum específico — por exemplo, ferritina, glicose, colesterol etc.`,
  ].join("\n");
}

function buildDocumentAnswer(
  matcher: ImagingMatcher,
  documents: DocumentRow[],
): string {
  const needles = matcher.patterns.map((p) => normalizeText(p).trim()).filter(
    Boolean,
  );

  const matches = documents.filter((doc) => {
    const haystack = normalizeText(
      `${doc.file_name || ""} ${doc.ai_summary || ""} ${doc.content_kind || ""}`,
    );
    return needles.some((needle) => haystack.includes(needle));
  });

  if (matches.length === 0) {
    const recent = documents.slice(0, 8).map((d) => `- ${d.file_name}`);
    const list = recent.length
      ? `\n\nSeus documentos mais recentes:\n${recent.join("\n")}`
      : "";
    return `Não encontrei nenhum documento de ${matcher.label} no seu histórico.${list}`;
  }

  const sorted = [...matches].sort(
    (a, b) => parseDateSafe(b.created_at) - parseDateSafe(a.created_at),
  );

  const latest = sorted[0];
  const extra = sorted.slice(1, 6);

  const header = `Encontrei ${matches.length} documento${matches.length > 1 ? "s" : ""} relacionado${matches.length > 1 ? "s" : ""} a ${matcher.label} no seu histórico.`;

  const latestBlock = [
    "",
    `Mais recente: ${latest.file_name} (${formatDatePtBr(latest.created_at)})`,
    latest.ai_summary ? `Resumo: ${latest.ai_summary.substring(0, 300)}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const extraBlock = extra.length
    ? `\n\nOutros documentos:\n${extra
        .map((d) => `- ${d.file_name} (${formatDatePtBr(d.created_at)})`)
        .join("\n")}`
    : "";

  return `${header}${latestBlock}${extraBlock}`;
}

export async function buildDeterministicAnswer(params: {
  userId: string;
  query: string;
}): Promise<DeterministicAnswer> {
  const { userId, query } = params;

  const imagingMatcher = detectImagingMatcher(query);
  const labKeyword = detectLabKeyword(query);
  const isGenericLab = matchesGenericLabQuery(query);

  if (!imagingMatcher && !labKeyword && !isGenericLab) {
    return { handled: false };
  }

  if (imagingMatcher) {
    const documents = await fetchDocuments(userId);
    const answer = buildDocumentAnswer(imagingMatcher, documents);
    return { handled: true, answer };
  }

  const patientIds = await resolvePatientIds(userId);
  if (patientIds.length === 0) return { handled: false };

  const labs = await fetchAllLabs(patientIds);
  if (labs === null) return { handled: false };

  if (labKeyword) {
    return {
      handled: true,
      answer: buildLabKeywordAnswer(labKeyword, labs),
    };
  }

  return {
    handled: true,
    answer: buildGenericLabAnswer(labs),
  };
}
